import { useEffect, useRef, useState } from "react";
import React from "react";
import { useLocation } from "react-router-dom";
import {
  differenceInDays,
  format,
  isSameWeek,
  isToday,
  isWeekend,
  isYesterday,
  lastDayOfWeek,
  setDefaultOptions,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import logger from "../utils/logger";

setDefaultOptions({ weekStartsOn: 1 });

const formatDate = (date, isDaySpecific = false) => {
  if (isToday(date) && isDaySpecific) {
    return "Today";
  }
  if (isYesterday(date) && isDaySpecific) {
    return "Yesterday";
  }

  const today = new Date();
  const suppliedDate = new Date(date);
  const diffInDays = differenceInDays(today, suppliedDate);
  // logger.log("diffInDays: ", diffInDays, " isSameWeek: ", isSameWeek(today, suppliedDate), " isWeekend: ", isWeekend(suppliedDate), " lastDayOfWeek: ", lastDayOfWeek(suppliedDate));

  if (diffInDays < 11 && isDaySpecific) {
    if (
      diffInDays < 8 &&
      !isSameWeek(today, suppliedDate) &&
      isWeekend(suppliedDate)
    ) {
      return "Last Weekend";
    }
    if (
      diffInDays > 1 &&
      !isSameWeek(today, suppliedDate) &&
      !isWeekend(suppliedDate)
    ) {
      return "Last Week";
    }
  }

  if (!isDaySpecific) {
    return format(date, "EEEE, MMMM d, yyyy");
  } else {
    return null;
  }
};

export default function RaceNavbar({
  selectedTrack = "",
  selectedRace = "",
  tracks,
  trackMap,
  onNavigate,
  selectedDate,
  availableDates = [],
  onDateChange,
  viewMode = "all-tracks",
  customOnNavigate = null,
  carouselRef = null,
}) {
  const location = useLocation();
  const isResultsPage = location.pathname === "/results";
  const isAllTipsPage = location.pathname === "/all-tips";

  const currentTrackIndex = tracks.indexOf(selectedTrack);
  const currentRace = parseInt(selectedRace);
  const currentTrackRaces = Array.from(trackMap.get(selectedTrack) || []).sort(
    (a, b) => a - b,
  );
  const maxRaceForCurrentTrack = currentTrackRaces.length
    ? Math.max(...currentTrackRaces)
    : 0;
  const minRaceForCurrentTrack = currentTrackRaces.length
    ? Math.min(...currentTrackRaces)
    : 0;

  const [isStacked, setIsStacked] = useState(false);
  const containerRef = useRef(null);
  const [displayedTrack, setDisplayedTrack] = useState(selectedTrack);
  const [displayedRace, setDisplayedRace] = useState(selectedRace);

  useEffect(() => {
    setDisplayedTrack(selectedTrack);
    setDisplayedRace(selectedRace);
  }, [selectedTrack, selectedRace]);

  const canGoNext = isResultsPage
    ? viewMode === "all-tracks"
      ? selectedDate &&
        new Date(selectedDate).setHours(0, 0, 0, 0) <
          new Date().setHours(0, 0, 0, 0) // Date is before today
      : viewMode === "single-race" &&
          format(selectedDate, "yyyy-MM-dd") ===
            format(new Date(), "yyyy-MM-dd")
        ? !(
            // Check if we're on the last race of the last track on today's data
            (
              currentTrackIndex === tracks.length - 1 &&
              currentRace === maxRaceForCurrentTrack
            )
          )
        : true // In track-races view or on other dates, always allow next
    : isAllTipsPage
      ? availableDates &&
        format(selectedDate, "yyyy-MM-dd") &&
        availableDates.indexOf(format(selectedDate, "yyyy-MM-dd")) <
          availableDates.length - 1
      : carouselRef?.current?.canNavigateNext() || true; // Allow navigation if carousel can navigate or always allow

  const canGoPrev = isResultsPage
    ? true
    : isAllTipsPage
      ? availableDates &&
        format(selectedDate, "yyyy-MM-dd") &&
        availableDates.indexOf(format(selectedDate, "yyyy-MM-dd")) > 0
      : carouselRef?.current?.canNavigatePrevious() || true; // Allow navigation if carousel can navigate or always allow

  useEffect(() => {
    const checkOverflow = () => {
      const container = containerRef.current;
      if (!container) return;

      // Check if content width exceeds 174px
      setIsStacked(container.scrollWidth > 250);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [selectedTrack, selectedDate]);

  const handleNavigation = React.useCallback(
    (direction) => {
      // logger.log("handleNavigation: direction: ", direction, ", isResultsPage: ", isResultsPage, ", customOnNavigate: ", customOnNavigate);
      // Common function to update URL without full page reload
      const updateUrlParams = (track, race) => {
        // Always update URL parameters without page reload regardless of path
        const url = new URL(window.location);
        const formattedTrack = track.replace(/\s+/g, "-").toLowerCase();
        url.searchParams.set("track", formattedTrack);
        url.searchParams.set("race", race);

        // Update URL without reloading the page
        window.history.pushState({}, "", url);

        // Always dispatch the urlchange event to ensure all components are notified
        window.dispatchEvent(
          new CustomEvent("urlchange", {
            detail: { track: formattedTrack, race: race, direction },
          }),
        );
      };

      if (isResultsPage && customOnNavigate) {
        // Call customOnNavigate first to update the parent state
        customOnNavigate(direction);

        // Then update local state based on the current track and race
        const currentTrackIndex = tracks.indexOf(selectedTrack);

        if (direction === "next") {
          if (currentRace < maxRaceForCurrentTrack) {
            // Stay on same track, next race
            const nextRace = (currentRace + 1).toString();
            setDisplayedRace(nextRace);
            updateUrlParams(selectedTrack, nextRace);
          } else if (currentTrackIndex < tracks.length - 1) {
            // Move to next track
            const nextTrack = tracks[currentTrackIndex + 1];
            const nextTrackRaces = Array.from(
              trackMap.get(nextTrack) || [],
            ).sort((a, b) => a - b);
            if (nextTrackRaces.length > 0) {
              setDisplayedTrack(nextTrack);
              setDisplayedRace(nextTrackRaces[0].toString());
              updateUrlParams(nextTrack, nextTrackRaces[0].toString());
            }
          }
        } else {
          if (currentRace > minRaceForCurrentTrack) {
            // Stay on same track, previous race
            const prevRace = (currentRace - 1).toString();
            setDisplayedRace(prevRace);
            updateUrlParams(selectedTrack, prevRace);
          } else if (currentTrackIndex > 0) {
            // Move to previous track
            const prevTrack = tracks[currentTrackIndex - 1];
            const prevTrackRaces = Array.from(
              trackMap.get(prevTrack) || [],
            ).sort((a, b) => a - b);
            if (prevTrackRaces.length > 0) {
              setDisplayedTrack(prevTrack);
              setDisplayedRace(Math.max(...prevTrackRaces).toString());
              updateUrlParams(
                prevTrack,
                Math.max(...prevTrackRaces).toString(),
              );
            }
          }
        }
        return;
      }

      if (isAllTipsPage) {
        // For all-tips page, only handle date navigation
        if (!selectedDate || !availableDates || !availableDates.length) return;

        const formattedCurrentDate = format(selectedDate, "yyyy-MM-dd");
        const currentDateIndex = availableDates.indexOf(formattedCurrentDate);

        if (
          direction === "next" &&
          currentDateIndex < availableDates.length - 1
        ) {
          const nextDate = availableDates[currentDateIndex + 1];
          if (!nextDate) return;

          const [year, month, day] = nextDate.split("-").map(Number);
          const newDate = new Date(year, month - 1, day);
          if (!isNaN(newDate.getTime())) {
            // Use client-side update instead of setter
            const url = new URL(window.location);
            url.searchParams.set("date", nextDate);
            window.history.pushState({}, "", url);
            window.dispatchEvent(
              new CustomEvent("datechange", { detail: { date: newDate } }),
            );
            onDateChange(newDate);
          }
        } else if (direction === "prev" && currentDateIndex > 0) {
          const prevDate = availableDates[currentDateIndex - 1];
          if (!prevDate) return;

          const [year, month, day] = prevDate.split("-").map(Number);
          const newDate = new Date(year, month - 1, day);
          if (!isNaN(newDate.getTime())) {
            // Use client-side update instead of setter
            const url = new URL(window.location);
            url.searchParams.set("date", prevDate);
            window.history.pushState({}, "", url);
            window.dispatchEvent(
              new CustomEvent("datechange", { detail: { date: newDate } }),
            );
            onDateChange(newDate);
          }
        }
        return;
      }

      if (isResultsPage) {
        if (direction === "next") {
          if (!canGoNext) return;

          if (currentRace < maxRaceForCurrentTrack) {
            const nextRace = (currentRace + 1).toString();
            setDisplayedRace(nextRace);
            updateUrlParams(selectedTrack, nextRace);
            onNavigate(selectedTrack, nextRace);
          } else if (currentTrackIndex < tracks.length - 1) {
            const nextTrack = tracks[currentTrackIndex + 1];
            const nextTrackRaces = Array.from(
              trackMap.get(nextTrack) || [],
            ).sort((a, b) => a - b);
            setDisplayedTrack(nextTrack);
            setDisplayedRace(nextTrackRaces[0].toString());
            updateUrlParams(nextTrack, nextTrackRaces[0].toString());
            onNavigate(nextTrack, nextTrackRaces[0].toString());
          } else {
            // Continue with date navigation code but update to use pushState
            if (!selectedDate || !availableDates || !availableDates.length)
              return;

            const formattedCurrentDate = format(selectedDate, "yyyy-MM-dd");
            const currentDateIndex =
              availableDates.indexOf(formattedCurrentDate);

            if (
              currentDateIndex === -1 ||
              currentDateIndex >= availableDates.length - 1
            )
              return;

            const nextDate = availableDates[currentDateIndex + 1];
            if (!nextDate) return;

            const [year, month, day] = nextDate.split("-").map(Number);
            const newDate = new Date(year, month - 1, day);

            if (isNaN(newDate.getTime())) return;

            // Use the datechange event to notify components
            const url = new URL(window.location);
            url.searchParams.set("date", nextDate);
            window.history.pushState({}, "", url);
            window.dispatchEvent(
              new CustomEvent("datechange", { detail: { date: newDate } }),
            );
            onDateChange(newDate, "first");
          }
        } else {
          if (!canGoPrev) return;

          if (currentRace > minRaceForCurrentTrack) {
            const prevRace = (currentRace - 1).toString();
            setDisplayedRace(prevRace);
            updateUrlParams(selectedTrack, prevRace);
            onNavigate(selectedTrack, prevRace);
          } else if (currentTrackIndex > 0) {
            const prevTrack = tracks[currentTrackIndex - 1];
            const prevTrackRaces = Array.from(
              trackMap.get(prevTrack) || [],
            ).sort((a, b) => a - b);
            setDisplayedTrack(prevTrack);
            setDisplayedRace(Math.max(...prevTrackRaces).toString());
            updateUrlParams(prevTrack, Math.max(...prevTrackRaces).toString());
            onNavigate(prevTrack, Math.max(...prevTrackRaces).toString());
          } else {
            // At first race of first track, move to previous day
            if (!selectedDate || !availableDates || !availableDates.length)
              return;

            const formattedCurrentDate = format(selectedDate, "yyyy-MM-dd");
            const currentDateIndex =
              availableDates.indexOf(formattedCurrentDate);

            if (currentDateIndex <= 0) return;

            const prevDate = availableDates[currentDateIndex - 1];
            if (!prevDate) return;

            // Parse the date string properly
            const [year, month, day] = prevDate.split("-").map(Number);
            const newDate = new Date(year, month - 1, day); // month is 0-based in JS Date

            if (isNaN(newDate.getTime())) return; // Check if date is valid

            // Use the datechange event to notify components
            const url = new URL(window.location);
            url.searchParams.set("date", prevDate);
            window.history.pushState({}, "", url);
            window.dispatchEvent(
              new CustomEvent("datechange", { detail: { date: newDate } }),
            );
            onDateChange(newDate, "last");
          }
        }
        return;
      }

      // Non-results page navigation - always use updateUrlParams
      if (direction === "next") {
        if (currentRace < maxRaceForCurrentTrack) {
          // Move to next race in current track
          const nextRace = (currentRace + 1).toString();
          setDisplayedRace(nextRace);
          updateUrlParams(selectedTrack, nextRace);
          onNavigate(selectedTrack, nextRace);
        } else if (currentTrackIndex < tracks.length - 1) {
          // Move to first race of next track
          const nextTrack = tracks[currentTrackIndex + 1];
          const nextTrackRaces = Array.from(trackMap.get(nextTrack) || []).sort(
            (a, b) => a - b,
          );
          setDisplayedTrack(nextTrack);
          setDisplayedRace(nextTrackRaces[0].toString());
          updateUrlParams(nextTrack, nextTrackRaces[0].toString());
          onNavigate(nextTrack, nextTrackRaces[0].toString());
        } else {
          // move to first race of first track
          const firstTrack = tracks[0];
          const firstTrackRaces = Array.from(
            trackMap.get(firstTrack) || [],
          ).sort((a, b) => a - b);
          if (firstTrackRaces.length > 0) {
            setDisplayedTrack(firstTrack);
            setDisplayedRace(firstTrackRaces[0].toString());
            updateUrlParams(firstTrack, firstTrackRaces[0].toString());
            onNavigate(firstTrack, firstTrackRaces[0].toString());
          }
        }
      } else {
        if (currentRace > minRaceForCurrentTrack) {
          // Move to previous race in current track
          const prevRace = (currentRace - 1).toString();
          setDisplayedRace(prevRace);
          updateUrlParams(selectedTrack, prevRace);
          onNavigate(selectedTrack, prevRace);
        } else if (currentTrackIndex > 0) {
          // Move to last race of previous track
          const prevTrack = tracks[currentTrackIndex - 1];
          const prevTrackRaces = Array.from(trackMap.get(prevTrack) || []).sort(
            (a, b) => a - b,
          );
          setDisplayedTrack(prevTrack);
          setDisplayedRace(Math.max(...prevTrackRaces).toString());
          updateUrlParams(prevTrack, Math.max(...prevTrackRaces).toString());
          onNavigate(prevTrack, Math.max(...prevTrackRaces).toString());
        } else {
          // At first race of first track, check if we can go to previous day
          if (selectedDate && availableDates && availableDates.length) {
            const formattedCurrentDate = format(selectedDate, "yyyy-MM-dd");
            const currentDateIndex =
              availableDates.indexOf(formattedCurrentDate);

            if (currentDateIndex > 0) {
              // We can go to previous day
              const prevDate = availableDates[currentDateIndex - 1];
              if (prevDate) {
                const [year, month, day] = prevDate.split("-").map(Number);
                const newDate = new Date(year, month - 1, day);

                if (!isNaN(newDate.getTime())) {
                  // Use the datechange event to notify components
                  const url = new URL(window.location);
                  url.searchParams.set("date", prevDate);
                  window.history.pushState({}, "", url);
                  window.dispatchEvent(
                    new CustomEvent("datechange", {
                      detail: { date: newDate },
                    }),
                  );
                  onDateChange(newDate, "last");
                  return;
                }
              }
            }
          }

          // If we can't go to previous day, wrap around to last race of last track
          const lastTrack = tracks[tracks.length - 1];
          const lastTrackRaces = Array.from(trackMap.get(lastTrack) || []).sort(
            (a, b) => a - b,
          );
          if (lastTrackRaces.length > 0) {
            setDisplayedTrack(lastTrack);
            setDisplayedRace(Math.max(...lastTrackRaces).toString());
            updateUrlParams(lastTrack, Math.max(...lastTrackRaces).toString());
            onNavigate(lastTrack, Math.max(...lastTrackRaces).toString());
          }
        }
      }
    },
    [
      isAllTipsPage,
      isResultsPage,
      selectedDate,
      availableDates,
      onDateChange,
      currentRace,
      maxRaceForCurrentTrack,
      currentTrackIndex,
      tracks,
      trackMap,
      selectedTrack,
      minRaceForCurrentTrack,
      onNavigate,
      canGoNext,
      canGoPrev,
      customOnNavigate,
      carouselRef,
    ],
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      // First check if any input element has focus to avoid capturing keystrokes when typing
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.isContentEditable;

      if (isInputFocused) return;

      // Only handle navigation if the buttons are enabled
      if (e.key === "ArrowLeft" && canGoPrev) {
        e.preventDefault(); // Prevent default browser scrolling

        // Use carousel navigation if provided and can navigate
        if (
          carouselRef &&
          carouselRef.current &&
          carouselRef.current.canNavigatePrevious()
        ) {
          carouselRef.current.navigateToPreviousRace();
        } else {
          // Fall back to regular navigation
          handleNavigation("prev");
        }
      } else if (e.key === "ArrowRight" && canGoNext) {
        e.preventDefault(); // Prevent default browser scrolling

        // Use carousel navigation if provided and can navigate
        if (
          carouselRef &&
          carouselRef.current &&
          carouselRef.current.canNavigateNext()
        ) {
          carouselRef.current.navigateToNextRace();
        } else {
          // Fall back to regular navigation
          handleNavigation("next");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNavigation, canGoPrev, canGoNext, carouselRef]);

  const getHeadingContent = () => {
    if (isAllTipsPage) {
      return (
        <>
          <div className="flex items-center justify-center min-w-0">
            <span className="raceNavBar-heading text-md font-bold text-deepPink font-italic truncate">
              {displayedTrack} &nbsp;
            </span>
            <span className="raceNavBar-subheading text-md font-bold whitespace-nowrap">
              {displayedRace && `R${displayedRace}`}
            </span>
          </div>
          <div className="text-xs font-normal text-gray-600 mt-1 cursor-pointer">
            {formatDate(selectedDate, false)}
          </div>
        </>
      );
    }

    if (isResultsPage) {
      if (viewMode === "all-tracks") {
        return (
          <>
            <div className="flex items-center justify-center min-w-0">
              <span className="raceNavBar-heading text-md font-bold text-deepPink font-italic truncate">
                {formatDate(selectedDate, false)}
              </span>
            </div>

            {formatDate(selectedDate, true) && (
              <div
                className={`text-xs font-normal text-gray-600 mt-1 cursor-pointer`}
              >
                {formatDate(selectedDate, true)}
              </div>
            )}
          </>
        );
      } else if (viewMode === "track-races") {
        return (
          <>
            <div className="flex items-center justify-center min-w-0">
              <span className="raceNavBar-heading text-md font-bold text-deepPink font-italic truncate">
                {displayedTrack}
              </span>
            </div>
            <div
              className="text-xs font-normal text-gray-600 mt-1 cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => {
                if (isResultsPage) {
                  const datePicker = document.querySelector(
                    '[aria-label="Pick a date"]',
                  );
                  if (datePicker) {
                    datePicker.click();
                  }
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && isResultsPage) {
                  e.preventDefault();
                  const datePicker = document.querySelector(
                    '[aria-label="Pick a date"]',
                  );
                  if (datePicker) {
                    datePicker.click();
                  }
                }
              }}
            >
              {formatDate(selectedDate)}
            </div>
          </>
        );
      } else if (viewMode === "single-race") {
        return (
          <>
            <div className="flex items-center justify-center min-w-0">
              <span className="raceNavBar-heading text-md font-bold text-deepPink font-italic truncate">
                {displayedTrack} &nbsp;
              </span>
              <span className="raceNavBar-subheading text-md font-bold whitespace-nowrap">
                {displayedRace && `R${displayedRace}`}
              </span>
            </div>
            <div className="text-xs font-normal text-gray-600 mt-1 cursor-pointer">
              {formatDate(selectedDate)}
            </div>
          </>
        );
      }
    }

    return (
      <>
        <div className="flex items-center justify-center min-w-0">
          <span className="raceNavBar-heading text-md font-bold text-deepPink font-italic truncate">
            {displayedTrack} &nbsp;
          </span>
          <span className="raceNavBar-subheading text-md font-bold whitespace-nowrap">
            {displayedRace && `R${displayedRace}`}
          </span>
        </div>

        <div
          className="text-xs font-normal text-gray-600 mt-1 cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => {
            if (isResultsPage || location.pathname === "/leaderboard") {
              // Simulate click on the date picker button
              const datePicker = document.querySelector(
                '[aria-label="Pick a date"]',
              );
              if (datePicker) {
                datePicker.click();
              }
            }
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === " ") &&
              (isResultsPage || location.pathname === "/leaderboard")
            ) {
              e.preventDefault();
              // Simulate click on the date picker button
              const datePicker = document.querySelector(
                '[aria-label="Pick a date"]',
              );
              if (datePicker) {
                datePicker.click();
              }
            }
          }}
        >
          {isResultsPage || location.pathname === "/leaderboard"
            ? formatDate(selectedDate)
            : "Today"}
        </div>
      </>
    );
  };

  return (
    <div className="race-nav-bar-container fixed bottom-[60px] left-0 right-0 bg-white border-t border-gray-200 flex items-center justify-between px-4 max-w-md mx-auto py-4">
      <button
        onClick={() => handleNavigation("prev")}
        className={`p-2 rounded-full border border-gray-200 ${
          canGoPrev ? "hover:bg-gray-100" : "opacity-10 cursor-not-allowed"
        }`}
        disabled={!canGoPrev}
        data-gtm-element-id="my-tips_navigation_button-1"
      >
        <ChevronLeft className="w-6 h-6 text-gray-600" />
      </button>

      <div className="text-center flex flex-col justify-center min-w-0">
        {getHeadingContent()}
      </div>

      <button
        onClick={() => handleNavigation("next")}
        className={`p-2 rounded-full border border-gray-200 ${
          canGoNext ? "hover:bg-gray-100" : "opacity-10 cursor-not-allowed"
        }`}
        disabled={!canGoNext}
        data-gtm-element-id="my-tips_navigation_button-2"
      >
        <ChevronRight className="w-6 h-6 text-gray-600" />
      </button>
    </div>
  );
}
